import { z } from 'zod';
import { CorrectionTypeSchema } from './attendance.schema.js';
import { FACE_MODEL_ID } from '../faceModel.js';

// ============================================================
// Field face-attendance API (docs/face-kiosk-plan/api-implemented.md, removed 2026-10-02 — in git history).
// These shapes are the wire contract between the rugged-Android capture
// app and /api/field/* — change them only in lockstep with the client.
// ============================================================

export const FieldRegisterRequestSchema = z.object({
  code: z.string().min(6).max(32),
  // The engine this device runs (`FaceCore.MODEL_ID`). Optional: an APK older
  // than Release B sends none, leaving Device.faceModelId null, which /sync
  // reads as the RETIRED engine — no templates, `modelMismatch`.
  faceModelId: z.string().min(1).max(64).optional(),
});
export type FieldRegisterRequest = z.infer<typeof FieldRegisterRequestSchema>;

// Lives in the zod-free `../errorCodes.js` so a client can read it without
// loading this schema module; re-exported here so existing imports still work.
export { SITE_WORKFORCE_DISABLED_CODE, UNKNOWN_WORKER_HELD_CODE } from '../errorCodes.js';

export const FieldSyncQuerySchema = z.object({
  // Unix millis; absent/0 means full pull.
  since: z.coerce.number().int().min(0).default(0),
  /* PAGING IS OPT-IN, AND ABSENCE MEANS TODAY'S EXACT BEHAVIOUR.
   *
   * The deployed APK's cursor IS `body.serverTime` and it has no notion of an
   * incomplete page: it applies whatever arrived and advances `lastSyncAt`. So a
   * server that volunteered a page boundary would leave every un-updated tablet
   * holding a SILENTLY TRUNCATED gallery it believes is complete — a worker
   * standing at a gate who cannot be recognised. `limit` is therefore never
   * inferred and never defaulted: only a client that asks for pages gets them,
   * and only such a client is shown `hasMore` / `nextCursor`. */
  limit: z.coerce.number().int().min(1).max(1000).optional(),
  /* Opaque keyset cursor from the previous page's `nextCursor`. It carries
   * (updatedAt, id) TOGETHER — see encodeSyncCursor in field.service.ts for why
   * a bare `updatedAt` loses workers at a page boundary. Needs `limit`. */
  cursor: z.string().min(1).max(256).optional(),
  correctionsSince: z.coerce.number().int().min(0).optional(), // NOT `since`; absent = 45-day window
});
export type FieldSyncQuery = z.infer<typeof FieldSyncQuerySchema>;

// One base64-encoded little-endian float32[192] = 768 bytes = 1024 b64 chars.
export const FieldTemplateB64Schema = z
  .string()
  .regex(/^[A-Za-z0-9+/]+={0,2}$/)
  .refine((s) => s.length === 1024, 'Template must encode exactly 768 bytes');

import { FieldManualVerifyShape } from './fieldManualVerify.schema.js';
import { FieldFixProviderSchema } from './fieldFixProvider.schema.js';
export const FieldEventSchema = z.object({
  uuid: z.string().min(8).max(64),
  workerCode: z.string().min(1).max(32).optional(),
  manualId: z.string().min(1).max(32).optional(),
  type: z.enum(['IN', 'OUT']),
  method: z.enum(['FACE', 'MANUAL_ID']),
  score: z.number().min(0).max(1),
  // Graded liveness SCORE at capture, in [0,1] (Release B: max of blink range,
  // head-pose range and 0.6 × displacement). OPTIONAL: unmeasured is a distinct
  // state, not 0 — sending 0 for it made the server flag every such punch as
  // "Low liveness 0.00". OMIT when unmeasured. Older APKs sent an eye-open value
  // here; fieldLivenessGate.gradedLiveness ignores those.
  liveness: z.number().min(0).max(1).optional(),
  ts: z.number().int().positive(), // client wall-clock, unix millis
  mono: z.number().int().nonnegative(), // elapsed-since-boot millis
  /**
   * serverTime − deviceTime when the punch was TAKEN, as the device measured it
   * (CaptureSkew.java, audit M-32). Absent from older APKs and wherever no sync
   * had measured the clock; the server flags a punch whose clock was more than
   * two minutes out (field.clockWindow.ts). Bounded to a year either way.
   */
  clockSkewMs: z.number().int().min(-31_536_000_000).max(31_536_000_000).optional(),
  sessionId: z.string().min(1).max(64),
  operatorCode: z.string().min(1).max(32).optional(),
  /**
   * The operator's name as TYPED on the device, in its PIN-protected Settings.
   *
   * Not the same fact as `operatorCode`. The code says which signed-in account
   * the punch ran under; the name says which person was holding the device —
   * and it rides kiosk punches too, where nobody signs in per punch. Trimmed,
   * and a blank or whitespace-only name becomes undefined, so "never typed"
   * and "typed spaces" are stored the same way: as no name at all.
   */
  operatorName: z
    .string()
    .trim()
    .max(80)
    .optional()
    .transform((v) => v || undefined),
  flag: z.string().max(190).optional(),
  lat: z.number().min(-90).max(90).optional(),
  lng: z.number().min(-180).max(180).optional(),
  /**
   * The fix's own radius, in metres, as the device's location API reported it.
   *
   * DECLARED HERE FOR THE FIRST TIME, WHERE IT WAS BEING SILENTLY DROPPED.
   * Every field device has computed and sent this on every GPS-carrying punch
   * since `PunchFacts.stamp` was written, and the operator's own evidence pane
   * shows it back to them ("Located to 24m") — but this schema never named the
   * field, so `validate.middleware`'s `safeParse` struck it from the body
   * before the service ever saw it, and `AttendanceEvent.accuracy` — a real,
   * already-migrated column — sat null for every FIELD punch. A reviewer could
   * not tell a fix pinned to 3m from one pinned to 3km: exactly the fact the
   * device itself relied on to trust the punch in the first place.
   */
  accuracy: z.number().nonnegative().optional(),
  /**
   * When the fix above was taken, unix millis — NOT when the punch was.
   * A device reuses its last good fix for a while rather than blocking a punch
   * on a cold GPS, so a fix can be minutes older than the punch it is stamped
   * on. Sent on every GPS-carrying punch since `PunchFacts.stamp`, and dropped
   * here with `accuracy` for the same reason: the schema never named it. Without
   * it a reviewer cannot tell "located here, now" from "located here, twenty
   * minutes before this punch".
   */
  gpsAt: z.number().int().positive().optional(),
  mock: z.boolean().optional(), // fix from a mock provider: never picks an AUTO tablet's site
  fixProvider: FieldFixProviderSchema, // gps | network | …: a network fix off-site is approximate
  /**
   * THE SITE THE OPERATOR CHOSE, on an AUTO tablet whose fix could not settle
   * between two of its sites. A CLAIM, never the authority: the server files the
   * punch there only when the site is in the tablet's group AND this punch's own
   * real (non-mock) fix is inside its fence; otherwise it files where the fix
   * says and flags the punch (`field.siteChoice.ts`). Ignored on a LOCKED tablet.
   * Absent from every older APK, whose punches are filed exactly as before.
   */
  chosenSiteId: z.string().min(1).max(64).optional(),
  // Assisted face-failure fallback (additive; omitting both = today's behaviour).
  // When a worker fails face match N times the operator manually selects them and
  // confirms; the resulting punch is a MANUAL_ID event carrying the uuid of the
  // failed face scan (so the punch links back to it) + a coded failure reason.
  failedScanUuid: z.string().min(8).max(64).optional(),
  failureReason: z.string().max(100).optional(), // e.g. 'MAX_ATTEMPTS' | 'NO_MATCH' | 'LIVENESS'
  // The captured face template (base64 float32[192], same encoding as enrolled
  // templates). Optional + additive: older devices omit it. When present and the
  // company runs field face review in 'verify' mode, the server RE-MATCHES this
  // against its own gallery to auto-promote a provisional punch (P3) — the
  // device's own `score` is never trusted for that decision.
  descriptor: FieldTemplateB64Schema.optional(), ...FieldManualVerifyShape, // + the typed number's 1:1 face check
});
export type FieldEvent = z.infer<typeof FieldEventSchema>;

export const FieldEventsRequestSchema = z.object({
  events: z.array(FieldEventSchema).min(1).max(100),
});
export type FieldEventsRequest = z.infer<typeof FieldEventsRequestSchema>;

// A correction raised on the field device by an operator on a worker's behalf
// (login-less camp workers can't self-submit). It rides the same device-token
// rails as events and lands as a PENDING Correction in the existing web
// approval queue. `uuid` is the device's idempotency key; `ts` is the requested
// punch time (client wall-clock, unix millis), which also fixes the day.
//
// WRONG_TIME and VOID_PUNCH are about ONE recorded punch, named by `eventUuid`
// — the device's own punch uuid, stored server-side as
// AttendanceEvent.idempotencyKey. Direction is never sent: the server reads it
// off that punch. `ts` is the new time for WRONG_TIME / MISSED_*, and is
// ignored for VOID_PUNCH (the voided punch's own time is stored instead).
const FIELD_CORRECTION_TS_REQUIRED = ['MISSED_PUNCH_IN', 'MISSED_PUNCH_OUT', 'WRONG_TIME'];
// WRONG_DIRECTION pins one punch too, and like VOID_PUNCH carries no new time.
const FIELD_CORRECTION_EVENT_REQUIRED = ['WRONG_TIME', 'VOID_PUNCH', 'WRONG_DIRECTION'];
export const FieldCorrectionSchema = z
  .object({
    uuid: z.string().min(8).max(64),
    workerCode: z.string().min(1).max(32),
    requestType: CorrectionTypeSchema.exclude(['ADD_BREAK']),
    ts: z.number().int().positive().optional(),
    eventUuid: z.string().min(8).max(64).optional(),
    reason: z.string().min(5).max(2000),
    operatorCode: z.string().min(1).max(32),
  })
  .superRefine((v, ctx) => {
    if (FIELD_CORRECTION_TS_REQUIRED.includes(v.requestType) && v.ts === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['ts'],
        message: `ts is required for ${v.requestType}`,
      });
    }
    if (FIELD_CORRECTION_EVENT_REQUIRED.includes(v.requestType) && v.eventUuid === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['eventUuid'],
        message: `eventUuid is required for ${v.requestType}`,
      });
    }
  });
export type FieldCorrection = z.infer<typeof FieldCorrectionSchema>;

export const FieldCorrectionsRequestSchema = z.object({
  corrections: z.array(FieldCorrectionSchema).min(1).max(100),
});
export type FieldCorrectionsRequest = z.infer<typeof FieldCorrectionsRequestSchema>;

/* WHAT KIND OF FACE A TEMPLATE WAS MADE FROM. Mirrors the Prisma
 * FieldTemplateOrigin enum; read its comment there for the full argument.
 *
 * Orthogonal to WHERE it was pushed from (source WEB/FIELD): a seed is a WEB
 * push and a harvest is a FIELD one, and they still differ in how much they can
 * be trusted at a door. Ordered by that trust, not alphabetically:
 * PHOTO_SEED < AUTO_HARVEST < LIVE_CAPTURE.
 *
 * AUTO_HARVEST is in the enum and on NO wire. The additive ingest it needs
 * (gallery cap, which frames qualify, drift detection) is a separate decision,
 * and a half-built self-confirming biometric write is the one thing worse than
 * not having one — see the Prisma comment. */
export const FieldTemplateOriginSchema = z.enum([
  'LIVE_CAPTURE',
  'PHOTO_SEED',
  'AUTO_HARVEST',
]);
export type FieldTemplateOriginCode = z.infer<typeof FieldTemplateOriginSchema>;

/* The lawful basis a template's consent record rests on (PDPL). Mirrors the
 * Prisma FieldConsentBasis enum. */
export const FieldConsentBasisSchema = z.enum([
  'PAPER_REGISTRATION',
  'HR_ATTESTED_CAPTURE',
  'HR_ATTESTED_PHOTO_REUSE',
  'INHERITED_FROM_ENROLMENT',
]);
export type FieldConsentBasisCode = z.infer<typeof FieldConsentBasisSchema>;

export const FieldEnrollRequestSchema = z
  .object({
    workerCode: z.string().min(1).max(32),
    templates: z.array(FieldTemplateB64Schema).min(1).max(10),
    modelId: z.literal(FACE_MODEL_ID), // required: a pre-alignment web bundle sends none

    // PDPL: the worker must have consented to face capture. The HR enrollment UI
    // sets this true after recording consent; /enroll rejects a falsy value.
    consent: z.literal(true, {
      errorMap: () => ({ message: 'Biometric consent is required to enroll a face' }),
    }),
    /* WHAT KIND OF FACE THESE TEMPLATES WERE MADE FROM.
     *
     * Defaults to LIVE_CAPTURE, which is what the deployed HR enrolment screen
     * does and has always done — a webcam capture with the employee in front of
     * it — so an existing client that sends no `origin` is unchanged.
     *
     * PHOTO_SEED is the bulk bootstrap: templates embedded from a photograph
     * already on file. It is accepted ONLY here, on the JWT/HR path, and never on
     * the device path — the paper consent a device relies on does not cover
     * reusing a visa scan, and a device has no user to attest that it does.
     *
     * AUTO_HARVEST is refused on this wire: it is not something a person asks
     * for, and nothing is built to write it. */
    origin: FieldTemplateOriginSchema.exclude(['AUTO_HARVEST']).default('LIVE_CAPTURE'),
    /* THE SEED'S OWN CONSENT RECORD, NAMED BY THE CALLER — never invented here.
     *
     * A photo on file was collected for an identity or visa record. Converting it
     * into a biometric template is a different purpose, so the registration paper
     * consent does not reach it and neither does the capture-time tick above,
     * which attests to a capture that did not happen. Somebody has to assert that
     * the worker consented to THIS, and the assertion has to be in the request
     * rather than assumed by the server.
     *
     * Required when `origin` is PHOTO_SEED and rejected otherwise — see the
     * refinement below. One permitted value on purpose: the point is that the
     * caller states the basis, not that they choose among several. */
    consentBasis: z.literal('HR_ATTESTED_PHOTO_REUSE').optional(),
    /* THE HR USER HAS SEEN THE DUPLICATE WARNING AND SAYS PROCEED.
     *
     * `/enroll` runs the same company-wide 1:N check the device path runs, and
     * refuses with 409 + the matched worker code when the incoming face already
     * belongs to somebody else. This is the second half of that conversation: it
     * can only be set by a caller who has already been shown who matched and how
     * strongly, which is why it defaults to false and is never inferred.
     *
     * It exists at all because the duplicate is sometimes real and sometimes
     * two genuinely different people — identical twins, on a workforce that has
     * them. The server cannot tell those apart and must not pretend to; a human
     * with both records in front of them can. The route audits the override, so
     * the decision carries the name of whoever made it. */
    overrideDedup: z.boolean().optional().default(false),
  })
  .superRefine((v, ctx) => {
    if (v.origin === 'PHOTO_SEED' && !v.consentBasis) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['consentBasis'],
        message:
          'Seeding a face template from a photo on file needs its own consent basis: ' +
          "set consentBasis to 'HR_ATTESTED_PHOTO_REUSE' to record that the worker " +
          'consented to their stored photograph being converted into a biometric ' +
          'template. The consent collected at registration does not cover this.',
      });
    }
    if (v.origin !== 'PHOTO_SEED' && v.consentBasis) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['consentBasis'],
        message:
          'consentBasis applies only to a PHOTO_SEED enrolment. A live capture ' +
          'records its basis from the consent tick, and letting the caller name a ' +
          'different one would let a seed be filed as a capture.',
      });
    }
  });
export type FieldEnrollRequest = z.infer<typeof FieldEnrollRequestSchema>;
// ============================================================
// Interactive DC roster (Slice 4). An operator marks a worker present/late/
// absent or records a break/out OFFLINE on the device; each mark rides the same
// device-token rails as /events and lands as a FLAGGED AttendanceEvent that rolls
// up through the AttendanceDay engine. ABSENT is a no-op ack (records no event).
// ============================================================
export const FieldRosterActionSchema = z.enum([
  'PRESENT',
  'LATE',
  'OUT',
  'ABSENT',
  'BREAK_START',
  'BREAK_END',
]);
export type FieldRosterAction = z.infer<typeof FieldRosterActionSchema>;

export const FieldRosterEventSchema = z.object({
  uuid: z.string().min(8).max(64),
  workerCode: z.string().min(1).max(32),
  action: FieldRosterActionSchema,
  // Optional per-day work site (camp tablets serve several sites); when absent
  // the event lands on the device's bound worksite.
  siteCode: z.string().min(1).max(32).optional(),
  ts: z.number().int().positive(), // device wall-clock, unix millis
  operatorCode: z.string().min(1).max(32).optional(),
  sessionId: z.string().min(1).max(64).optional(),
  // APK-OT (E6 on the tablet): a DC-entered overtime override (minutes) for this
  // worker/day, independent of the PRESENT/OUT mark. Ingest writes it onto the
  // day as AttendanceDay.manualOvertimeMinutes and recomputes; the rollup then
  // pins overtimeMinutes to this value. Capped at 12h to match the web roster.
  otMinutes: z.number().int().min(0).max(720).optional(),
  // E3: LATE and ABSENT marks require the operator to record WHY. Optional on
  // the wire (other actions don't need it); the service enforces presence for
  // LATE/ABSENT and stores it on the event flaggedReason (or, for ABSENT, the
  // ACK note, since ABSENT writes no event).
  reason: z.string().min(1).max(500).optional(),
});
export type FieldRosterEvent = z.infer<typeof FieldRosterEventSchema>;

export const FieldRosterEventsRequestSchema = z.object({
  overrides: z.array(FieldRosterEventSchema).min(1).max(100),
});
export type FieldRosterEventsRequest = z.infer<typeof FieldRosterEventsRequestSchema>;

// ============================================================
// A TRANSFER IS A DEPARTURE, NOT A RE-ASSIGNMENT.
//
// It used to be both halves at once: the operator picked a destination and the
// server wrote a SITE_OUT here plus a SITE_IN there, at the same instant. Two
// things were wrong with that, and the smaller one was the accuracy of the
// destination.
//
// The larger one is that the gate operator does not decide where the worker
// ends up. They get in a truck and the truck goes where it goes. So the app was
// recording an ARRIVAL that nobody had witnessed, at a site the worker had not
// reached, on the word of someone standing somewhere else — and every screen
// downstream then showed them on site B while they were still on the road.
//
// So a transfer now records ONE event: a face-proved PUNCH_OUT at this device's
// site, marked `transferOut` — "released, still working". The arrival is
// recorded by whichever gate they actually reach, by the same face scan as any
// other check-in. The gap between the two is paid inter-site travel and is
// computed by the rollup (attendanceDay.pairing), which is the only place that
// can see both ends.
//
// `uuid` is both the device idempotency key and the event's, so the face crop
// uploads to /photos/<uuid> on the ordinary photo rails.
// ============================================================
export const FieldTransferSchema = z.object({
  uuid: z.string().min(8).max(64),
  workerCode: z.string().min(1).max(32),
  // The site the worker is currently checked-in at (the device's bound site).
  fromWorksiteId: z.string().min(1),
  /* NO DESTINATION. A device still holding a queued draft from the two-event
   * model keeps sending `toWorksiteId`; rejecting it would 400 the whole batch
   * and wedge every later transfer behind one stale draft. It is not declared
   * here, and that is what accepts it: z.object STRIPS an unknown key rather
   * than refusing it, so the stale draft parses and the destination is dropped
   * on the floor. Declaring it "accepted and ignored" was a settings field
   * nothing read (check:inertsettings passed only on a comment — audit
   * 2026-10-15, M-62). Never make this object .strict(): field.transfer.test.ts
   * pins the stale draft parsing. */
  ts: z.number().int().positive(), // transfer time, client wall-clock unix millis
  /**
   * A face was captured for this worker at this gate, and its crop will follow
   * on the photo rails. REQUIRED, and refused when false: an operator ticking
   * names off a list is the assertion this whole flow was rebuilt to stop
   * accepting, and "ID and image" is the evidence standard for a movement
   * nobody re-verifies later.
   */
  faceProved: z.boolean(),
  /**
   * HOW THE WORKER WAS IDENTIFIED. Absent on an older app, which means FACE.
   * `MANUAL_ID` is the one case `faceProved: false` is accepted: the operator
   * typed the worker's number, a face photo was still taken (it follows on the
   * photo rails), and the departure is recorded as MANUAL_ID and FLAGGED for
   * review — never as a face match. Enforced per item in field.transfer.ts, not
   * here, so one bad draft is a REJECTED row rather than a 400 for the batch.
   */
  method: z.enum(['FACE', 'MANUAL_ID']).optional(),
  /** The digits the operator typed. Required with `method: 'MANUAL_ID'`. */
  manualId: z.string().min(1).max(32).optional(),
  ...FieldManualVerifyShape, // the 1:1 face check behind a typed pick
  score: z.number().min(0).max(1).optional(), // a FACE pick's match score: evidence on the meta row, decides nothing
  operatorCode: z.string().min(1).max(32).optional(),
  reason: z.string().min(1).max(500).optional(),
});
export type FieldTransfer = z.infer<typeof FieldTransferSchema>;

export const FieldTransfersRequestSchema = z.object({
  transfers: z.array(FieldTransferSchema).min(1).max(100),
});
export type FieldTransfersRequest = z.infer<typeof FieldTransfersRequestSchema>;

export const FieldRosterEventResultSchema = z.object({
  uuid: z.string(),
  status: z.enum(['ACKED', 'DUPLICATE', 'REJECTED', 'ERROR']),
  reason: z.string().optional(),
});
export type FieldRosterEventResult = z.infer<typeof FieldRosterEventResultSchema>;

export const FieldRosterEventsResponseSchema = z.object({
  results: z.array(FieldRosterEventResultSchema),
});
export type FieldRosterEventsResponse = z.infer<typeof FieldRosterEventsResponseSchema>;

// The deployable work sites a camp device may attribute a roster mark to.
export const FieldSitesForCampResponseSchema = z.object({
  sites: z.array(
    z.object({
      code: z.string(),
      name: z.string(),
      glCode: z.string().optional(),
    }),
  ),
});
export type FieldSitesForCampResponse = z.infer<typeof FieldSitesForCampResponseSchema>;

// E10 device policy push: the server-resolved faceRecognition policy returned in
// the /sync payload so the device persists it over its local Settings.
export const FieldFacePolicySchema = z.object({
  minConfidence: z.number().min(0).max(1),
  margin: z.number().min(0).max(1),
  livenessFloor: z.number().min(0).max(1),
  maxAttempts: z.number().int().min(1).max(20),
  fallbackToPin: z.boolean(),
  /**
   * `faceRecognition.requiredOnKiosk` — face only on an UNATTENDED panel, where
   * the operator attestation the MANUAL_ID path rests on does not exist.
   *
   * OPTIONAL and NOT defaulted, deliberately: the device must be able to tell an
   * explicit `false` from a server too old to have an opinion, because it
   * resolves absence per surface. The argument is in `manualIdAllowedBy`
   * (`apps/field/src/sync/facePolicy.ts`); it is not repeated here.
   */
  requiredOnKiosk: z.boolean().optional(),
  // P3: 'verify' makes FACE punches provisional until the server re-matches them.
  reviewMode: z.enum(['auto_count', 'verify']).default('auto_count'),
  // Margin the claimed worker must beat the next-closest by ('verify' auto-promotion).
  // Spelled as the setting it carries (`faceRecognition.fieldReverifyMargin`):
  // a rename here made the server's one consumer invisible to check:inertsettings,
  // which passed only because a comment named the setting (audit 2026-10-15, M-62).
  fieldReverifyMargin: z.number().min(0).max(1).default(0.04),
  // Enrolment dedup floors for the device pre-check; absent from an older server → defaults.
  dedupWarnFloor: z.number().min(0).max(1).optional(),
  dedupBlockFloor: z.number().min(0).max(1).optional(),
});
export type FieldFacePolicy = z.infer<typeof FieldFacePolicySchema>;

/* THE TWO-LAYER APP LOCK lives in `field.appLock.schema.ts` — the asymmetry
   between the two PINs is a page of reasoning and it belongs beside the two
   schemas it explains, not in the middle of the device sync payload. */
export {
  FieldAppLockSchema,
  FieldAppLockVerifySchema,
  type FieldAppLock,
} from './field.appLock.schema.js';
